import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, retry, throwError } from 'rxjs';
import {
  FutureAllocation,
  FutureAllocationSummary,
  BulkCreateFutureAllocation
} from '../models/future-allocation.model';
import { InvoiceType, Month, AllocationStatus } from '../models/allocation.model';
import { environment } from '../../environments/environment';

export interface CreateFutureAllocationDto {
  employeeId: string;
  roleId?: string;
  projectId: string;
  invoiceType: InvoiceType;
  costCenterId?: string;
  serviceGroupId?: string;
  year: number;
  month: Month;
  percentage: number;
  status?: AllocationStatus;
  comments?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FutureAllocationService {
  private apiUrl = `${environment.apiUrl}/FutureAllocation`;

  constructor(private http: HttpClient) { }

  getByWindow(startYear: number, startMonth: number, months: number): Observable<FutureAllocation[]> {
    return this.http.get<FutureAllocation[]>(`${this.apiUrl}/${startYear}/${startMonth}/${months}`).pipe(
      retry(2),
      catchError(error => this.handleError(error, 'Failed to load future allocations'))
    );
  }

  getSummary(startYear: number, startMonth: number, months: number): Observable<FutureAllocationSummary[]> {
    return this.http.get<FutureAllocationSummary[]>(`${this.apiUrl}/summary/${startYear}/${startMonth}/${months}`).pipe(
      catchError(() => of([]))
    );
  }

  create(dto: CreateFutureAllocationDto): Observable<FutureAllocation> {
    return this.http.post<FutureAllocation>(this.apiUrl, dto).pipe(
      catchError(error => this.handleError(error, 'Failed to create future allocation'))
    );
  }

  createBulk(dto: BulkCreateFutureAllocation): Observable<FutureAllocation[]> {
    return this.http.post<FutureAllocation[]>(`${this.apiUrl}/bulk`, dto).pipe(
      catchError(error => this.handleError(error, 'Failed to save future allocations'))
    );
  }

  update(id: string, allocation: FutureAllocation): Observable<FutureAllocation> {
    return this.http.put<FutureAllocation>(`${this.apiUrl}/${id}`, allocation).pipe(
      catchError(error => this.handleError(error, 'Failed to update future allocation'))
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => this.handleError(error, 'Failed to delete future allocation'))
    );
  }

  exportToExcel(startYear: number, startMonth: number, months: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/export-excel/${startYear}/${startMonth}/${months}`, {
      responseType: 'blob'
    }).pipe(
      catchError(error => this.handleError(error, 'Failed to export to Excel'))
    );
  }

  private handleError(error: any, message: string): Observable<never> {
    // Surface the server's business-rule message (422) when present, else a friendly fallback.
    const serverMessage = error?.error?.details || error?.error?.message;
    console.error(`${message}:`, error);
    return throwError(() => new Error(serverMessage || `${message}. Please try again.`));
  }
}
